import { initFancybox } from "../../../../../../themes/growtype/resources/scripts/plugins/fancybox/initFancybox";
import { messageReturnFinishedEvent } from "../events/imageReturnFinishedEvent";
import { scrollToChatBottom } from "./scrollToChatBottom";
import { chatSettingsBtn } from "./chatSettingsBtn";
import { renderAudio } from "./renderAudio";
import { initMessageActions } from "./initMessageActions";
import { typingAnimationHTML, getTypingQueue } from "./typingAnimation";
import { messageQueue } from "./messageQueue";
import { mediaReady } from "./mediaReady";
import { initVideoLoaders } from "./initVideoLoaders";
import { initMessageLoader } from "./initMessageLoader";
import { hideMessageLoader } from "./hideMessageLoader";
import { autoPlayMessage } from "./playMessage";
import { reconcileMessageGrouping } from "./reconcileMessageGrouping";
import { setupQuiz } from "./questionQuiz";

/**
 * Global audio queue to ensure sequential playback across all responses
 */
let audioPlaybackQueue = Promise.resolve();

function normalizeJsonLikeMessageContent(messageElement) {
  const contentInner = messageElement.find(".g-chatform-message-content-inner");

  if (!contentInner.length) {
    return;
  }

  const rawText = contentInner.text().trim();

  if (!rawText.startsWith("{") || !rawText.endsWith("}")) {
    return;
  }

  try {
    const decoded = JSON.parse(rawText);
    const replacementText =
      decoded?.message || decoded?.text || decoded?.narrative;

    if (typeof replacementText === "string" && replacementText.trim() !== "") {
      const paragraphs = replacementText
        .split(/\n\s*\n/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean)
        .map((paragraph) => `<p>${paragraph.replace(/\n/g, "<br>")}</p>`)
        .join("");

      contentInner.html(paragraphs || `<p>${replacementText}</p>`);
    }
  } catch (error) {
    // Keep original content if it wasn't valid JSON after all.
  }
}

function shouldRevealContentBeforeLoader(messageElement) {
  const contentInner = messageElement.find(".g-chatform-message-content-inner");

  if (!contentInner.length) {
    return false;
  }

  return (
    contentInner.find(
      ".growtype-quiz-wrapper, form, .quiz, .poll, .g-chat-quiz-chips, .g-chatform-message-assets-wrapper, .g-chatform-message-img-wrapper, .audio-message, video, .gift-wrapper"
    ).length > 0
  );
}

function initializeRichMessageContent(messageElement) {
  messageElement
    .find(".growtype-theme-fancybox")
    .each(function (index, element) {
      initFancybox(jQuery(element));
    });

  messageElement.find(".growtype-quiz-wrapper").each(function () {
    const quizWrapper = jQuery(this);

    if (quizWrapper.attr("data-growtype-chat-quiz-initialized") === "true") {
      return;
    }

    setupQuiz(quizWrapper);
    quizWrapper.attr("data-growtype-chat-quiz-initialized", "true");
  });

  if (messageElement.find(".audio-message").length > 0) {
    renderAudio(messageElement.find(".audio-message"));
  }

  if (
    messageElement.find(".lazy-load").length > 0 &&
    typeof $.fn.Lazy === "function"
  ) {
    messageElement.find(".lazy-load").Lazy();
  }

  if (messageElement.find(".btn-chat-settings").length > 0) {
    chatSettingsBtn(messageElement.find(".btn-chat-settings"));
  }

  messageElement.find("video").each(function () {
    this.muted = true;

    if (this.paused) {
      this.load?.();
      this.play().catch((e) =>
        console.warn("Video play failed after message render:", e)
      );
    }
  });
}

export function renderMessage(chatForm, response, userMessageTemplate = null) {
  const chatRoot = chatForm.hasClass("g-chatform")
    ? chatForm
    : chatForm.closest(".g-chatform");
  const contentWrapper = chatForm.hasClass("g-chatform-content")
    ? chatForm
    : chatForm.find(".g-chatform-content");
  let responseMessages = response.data.response_messages ?? "";
  let redirectUrl = response.data.redirect_url ?? "";
  let submitBtn = chatRoot.find(".g-chatform-footer-submit-wrapper .btn-send");

  if (responseMessages && responseMessages.length > 0) {
    // We will remove userMessageTemplate (initial loader) inside the loop
    // to ensure dots stay visible while intermediate messages are shown.

    let messageChain = Promise.resolve();

    responseMessages.forEach(function (element, index) {
      let initialDelay =
        element["delay"] !== undefined ? element["delay"] : 100;
      if (index === 0) initialDelay = 0;

      // Chain this message to wait for previous message to complete
      messageChain = messageChain.then(async () => {
        // Wait for any existing typing to fully complete before starting the next message
        await getTypingQueue();

        return new Promise((resolve) => {
          // Wait for the initial delay before showing message
          setTimeout(() => {
            let newMessage = $(element["html"]);
            newMessage.addClass("is-new-message");
            normalizeJsonLikeMessageContent(newMessage);

            initMessageActions(newMessage);

            const isUserRole = newMessage.attr("data-role") === "user";
            const shouldShowContentBeforeLoader =
              shouldRevealContentBeforeLoader(newMessage);

            if (!shouldShowContentBeforeLoader) {
              // Hide content initially to prevent flash of full text
              const contentInner = newMessage.find(
                ".g-chatform-message-content-inner"
              );
              contentInner.hide();
            }

            // If this is the first message in the response, remove the initial thinking loader
            // to replace it with the actual response message bubbles.
            if (
              index === 0 &&
              userMessageTemplate &&
              userMessageTemplate.closest("body").length
            ) {
              // A later submission may already have appended messages below this
              // request's loader. Move only this request-owned placeholder to the
              // current end so its completed response cannot appear above them.
              userMessageTemplate.detach();
              contentWrapper.append(userMessageTemplate);
              userMessageTemplate.replaceWith(newMessage);
              userMessageTemplate = null;
            } else {
              // Show immediately and init video loaders
              contentWrapper.append(newMessage);
            }

            reconcileMessageGrouping(contentWrapper);

            if (shouldShowContentBeforeLoader) {
              newMessage.find(".g-chatform-message-content-inner").show();
              initializeRichMessageContent(newMessage);
            } else {
              // This is a thinking loader message
              newMessage.find(".g-chatform-message-content-inner").hide();
              scrollToChatBottom(200, true);

              // Show loader dots while media is buffering or waiting to type
              initMessageLoader(newMessage, chatForm);
            }

            // Chain media waiting for the rest of the promise chain (like audio/typing)
            const readyPromise = mediaReady(newMessage);

            if (isUserRole) {
              // Scroll to bottom + some extra space to anticipate typing
              scrollToChatBottom(400, true);

              readyPromise
                .then(() => {
                  if (shouldShowContentBeforeLoader) {
                    hideMessageLoader(newMessage);
                    scrollToChatBottom(500, true);

                    document.dispatchEvent(
                      new CustomEvent("typingAnimationComplete", {
                        detail: {
                          message: newMessage[0],
                          messageData: element,
                          response: response,
                          messageIndex: index,
                        },
                      })
                    );

                    setTimeout(resolve, 300);
                    return;
                  }

                  return typingAnimationHTML(newMessage);
                })
                .then(() => {
                  if (shouldShowContentBeforeLoader) {
                    return;
                  }

                  // Re-initialize dynamic components after DOM is stable
                  initializeRichMessageContent(newMessage);

                  if (newMessage.find(".audio-message").length > 0) {
                    const audioEl = newMessage.find(".audio-message");
                    console.log(
                      "renderMessage: Audio element found, automatic_voice_messages =",
                      response.data.automatic_voice_messages
                    );

                    // Queue audio for automatic playback if enabled
                    if (response.data.automatic_voice_messages) {
                      console.log(
                        "renderMessage: Queueing audio for automatic playback"
                      );
                      audioPlaybackQueue = audioPlaybackQueue.then(() => {
                        return new Promise((resolve) => {
                          const startPlayback = () => {
                            audioEl.addClass("is-playing");
                            const ws = audioEl[0].waveSurfer;
                            if (ws) {
                              ws.play();
                              ws.once("finish", () => {
                                audioEl.removeClass("is-playing");
                                setTimeout(resolve, 300); // Tiny pause between clips
                              });
                            } else {
                              resolve();
                            }
                          };

                          // If already buffered/ready, play immediately, otherwise wait
                          if (audioEl[0].audioIsReady) {
                            startPlayback();
                          } else {
                            audioEl.one(
                              "growtypeChatAudioReady",
                              startPlayback
                            );
                          }
                        });
                      });
                    }
                  }

                  // Auto-play TTS if enabled (independent of embedded audio)
                  if (response.data.automatic_voice_messages) {
                    console.log(
                      "renderMessage: Auto-play TTS enabled, triggering playback"
                    );
                    audioPlaybackQueue = audioPlaybackQueue.then(() => {
                      return autoPlayMessage(newMessage);
                    });
                  }

                  // Animation complete, scroll to actual bottom
                  scrollToChatBottom(500, true);

                  // Dispatch event that typing animation is complete
                  document.dispatchEvent(
                    new CustomEvent("typingAnimationComplete", {
                      detail: {
                        message: newMessage[0],
                        messageData: element, // Include the message data
                        response: response,
                        messageIndex: index,
                      },
                    })
                  );

                  // Resolve to next message after a short pause
                  hideMessageLoader(newMessage);
                  setTimeout(resolve, 300);
                });
            } else {
              readyPromise.then(() => {
                if (shouldShowContentBeforeLoader) {
                  hideMessageLoader(newMessage);
                  scrollToChatBottom(500, true);
                  setTimeout(resolve, 300);
                  return;
                }

                // For non-user messages (admin), show content immediately
                hideMessageLoader(newMessage);
                newMessage
                  .find(".g-chatform-message-content-inner")
                  .fadeIn(300);

                // For non-user messages, initialize everything immediately
                initializeRichMessageContent(newMessage);

                scrollToChatBottom(500, true);

                // Resolve to next message after a short pause
                setTimeout(resolve, 300);
              });
            }

            /**
             * Dispatch event
             */
            document.dispatchEvent(
              messageReturnFinishedEvent({
                response: response,
                element: element,
                userMessageTemplate: newMessage,
              })
            );

            // Remove loading state on last message
            if (index === responseMessages.length - 1) {
              submitBtn.removeClass("is-loading");
            }
          }, initialDelay);
        });
      });
    });

    return messageChain.then(() => {
      // Handle redirect after all messages complete
      if (redirectUrl) {
        setTimeout(function () {
          window.location.href = redirectUrl;
        }, 1000);
      }
    });
  } else {
    if (userMessageTemplate) {
      userMessageTemplate.remove();
    }

    submitBtn.removeClass("is-loading");
  }
}
